const DISPLAY_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "long",
  year: "numeric",
};

const YYYY_MM_DD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Format a Date as YYYY-MM-DD in the user's local timezone (not UTC). */
export function toLocalDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD as local midnight. */
export function parseLocalDateInput(yyyyMmDd: string): Date | null {
  if (!YYYY_MM_DD_RE.test(yyyyMmDd)) return null;
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

/** Normalize ISO or YYYY-MM-DD Redux values back to local YYYY-MM-DD for UI. */
export function parseStoredDateToLocalInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (YYYY_MM_DD_RE.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  return toLocalDateInput(parsed);
}

/** Convert YYYY-MM-DD to "DD Month YYYY" for DatePickerCalendar.selectedDate. */
export function localInputToDisplayDate(yyyyMmDd: string): string {
  const parsed = parseLocalDateInput(yyyyMmDd);
  if (!parsed) return "";
  return parsed.toLocaleDateString("en-GB", DISPLAY_DATE_FORMAT);
}

/** Convert calendar display output ("DD Month YYYY") back to YYYY-MM-DD. */
export function displayDateToLocalInput(display: string): string {
  const trimmed = display.trim();
  if (!trimmed) return "";
  if (YYYY_MM_DD_RE.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  return toLocalDateInput(parsed);
}

/** Today's date as YYYY-MM-DD in local timezone. */
export function getTodayLocalInput(): string {
  return toLocalDateInput(new Date());
}

/** Compare two YYYY-MM-DD strings. Returns negative if a < b, 0 if equal, positive if a > b. */
export function compareLocalDateInputs(a: string, b: string): number {
  return a.localeCompare(b);
}

export interface CustomDateRangeValidationOptions {
  /** When true, start/end may be after today (e.g. task due dates). */
  allowFutureDates?: boolean;
}

/** Validate custom range: start <= end, and both <= today unless future dates are allowed. */
export function isValidCustomDateRange(
  start: string,
  end: string,
  options?: CustomDateRangeValidationOptions
): boolean {
  if (!start || !end) return false;
  if (!parseLocalDateInput(start) || !parseLocalDateInput(end)) return false;
  if (!options?.allowFutureDates) {
    const today = getTodayLocalInput();
    if (compareLocalDateInputs(start, today) > 0) return false;
    if (compareLocalDateInputs(end, today) > 0) return false;
  }
  if (compareLocalDateInputs(end, start) < 0) return false;
  return true;
}

/** Like isValidCustomDateRange, optionally enforcing a maximum span in days. */
export function isValidCustomDateRangeWithMax(
  start: string,
  end: string,
  maxRangeDays?: number,
  options?: CustomDateRangeValidationOptions
): boolean {
  if (!isValidCustomDateRange(start, end, options)) return false;
  if (
    maxRangeDays != null &&
    daysBetweenLocalInputs(start, end) > maxRangeDays
  ) {
    return false;
  }
  return true;
}

/** Days between two local YYYY-MM-DD values (end - start). */
export function daysBetweenLocalInputs(start: string, end: string): number {
  const startDate = parseLocalDateInput(start);
  const endDate = parseLocalDateInput(end);
  if (!startDate || !endDate) return NaN;
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((endDate.getTime() - startDate.getTime()) / msPerDay);
}

/** Preset range: N days back from today (inclusive). Returns [start, end] as YYYY-MM-DD. */
export function getBoundedPresetRange(daysBack: number): {
  start: string;
  end: string;
} {
  const today = new Date();
  const end = toLocalDateInput(today);
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - (daysBack - 1));
  return { start: toLocalDateInput(startDate), end };
}

/** Preset range: N days back from today, from-only (no end). */
export function getFromOnlyPresetStart(daysBack: number): string {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - daysBack);
  return toLocalDateInput(startDate);
}

/**
 * Normalize a UI date filter value to YYYY-MM-DD (calendar date picked in the UI).
 */
function toCalendarYmd(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const local = parseStoredDateToLocalInput(trimmed);
  if (!local || !YYYY_MM_DD_RE.test(local)) return undefined;
  return local;
}

/**
 * Convert a UI date filter to API start-of-IST-calendar-day as UTC ISO.
 * Selected day is India (Asia/Kolkata) midnight, not UTC midnight.
 * e.g. 2026-10-02 → 2026-10-01T18:30:00.000Z
 */
export function toApiDateRangeStart(value: string): string | undefined {
  const ymd = toCalendarYmd(value);
  if (!ymd) return undefined;
  const ms = Date.parse(`${ymd}T00:00:00+05:30`);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString();
}

/**
 * Convert a UI date filter to API end-of-IST-calendar-day as UTC ISO.
 * Selected day is India (Asia/Kolkata) end-of-day, not UTC end-of-day.
 * e.g. 2026-10-02 → 2026-10-02T18:29:59.999Z
 */
export function toApiDateRangeEnd(value: string): string | undefined {
  const ymd = toCalendarYmd(value);
  if (!ymd) return undefined;
  const ms = Date.parse(`${ymd}T23:59:59.999+05:30`);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString();
}
